import {
  Area,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  ComposedChart,
} from "recharts";
import { formatMoney } from "@/lib/format";
import { useTheme } from "@/lib/ThemeContext";
import { TEST_IDS } from "@/testIds";

function CustomTooltip({ active, payload, label, theme }) {
  if (!active || !payload?.length) return null;
  const proj = payload.find((p) => p.dataKey === "projected");
  const act = payload.find((p) => p.dataKey === "actual");
  const point = payload[0]?.payload || {};
  const hasActual = act?.value != null && act.value > 0;
  const isDark = theme === "dark";
  return (
    <div
      className="rounded-lg px-3.5 py-2.5 min-w-[180px]"
      style={{
        background: isDark ? "rgba(20, 22, 28, 0.92)" : "rgba(255, 255, 255, 0.96)",
        border: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(11,18,32,0.10)"}`,
        backdropFilter: "blur(12px)",
        boxShadow: "0 10px 30px rgba(0,0,0,0.20)",
        color: isDark ? "#F5F5F7" : "#0B1220",
      }}
    >
      <div className="text-[11px] uppercase tracking-widest mb-2" style={{ color: isDark ? "#71717A" : "#8B93A3" }}>
        {label}
      </div>
      <div className="flex items-center justify-between gap-6 mb-1">
        <span className="text-xs" style={{ color: isDark ? "#A1A1AA" : "#48526B" }}>Projected</span>
        <span className="num text-sm font-semibold" style={{ color: "#F43F5E" }}>
          {formatMoney(proj?.value)}
        </span>
      </div>
      {hasActual && (
        <div className="flex items-center justify-between gap-6">
          <span className="text-xs" style={{ color: isDark ? "#A1A1AA" : "#48526B" }}>Actual</span>
          <span className="num text-sm font-semibold">{formatMoney(act?.value)}</span>
        </div>
      )}
      {point.milestone_marker && (
        <div className="mt-2 pt-2 text-[10px] uppercase tracking-widest" style={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.10)" : "rgba(11,18,32,0.08)"}`, color: "#F43F5E" }}>
          Milestone · {formatMoney(point.milestone_amount)}
        </div>
      )}
    </div>
  );
}

const MilestoneDot = ({ isDark }) => (props) => {
  const { cx, cy, payload } = props;
  if (!payload?.milestone_marker) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={5} fill={isDark ? "#0B0F1A" : "#FFFFFF"} stroke="#F43F5E" strokeWidth={2} />
      <circle cx={cx} cy={cy} r={2} fill="#F43F5E" />
    </g>
  );
};

export default function CollectionsChart({ timeline, projectionOnly = false, title, kicker }) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const data = (timeline || []).map((t) => ({ ...t }));
  const hasActual = !projectionOnly && data.some((d) => d.actual > 0);

  const gridStroke = isDark ? "rgba(255,255,255,0.06)" : "rgba(11,18,32,0.06)";
  const axisText = isDark ? "#71717A" : "#8B93A3";
  const axisLine = isDark ? "rgba(255,255,255,0.10)" : "rgba(11,18,32,0.10)";
  const actualStroke = isDark ? "#F5F5F7" : "#0B1220";

  return (
    <div
      data-testid={TEST_IDS.chartTimeline}
      className="cf-card cf-fade-up p-5 md:p-6"
    >
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-light font-medium">
            {kicker || "Collections Timeline"}
          </div>
          <div className="mt-1 font-display text-lg text-ink">
            {title || `Projected ${hasActual ? "vs Actual " : ""}collections across horizon`}
          </div>
        </div>
        <div className="flex items-center gap-4 text-xs text-muted-dark">
          <div className="flex items-center gap-2">
            <span className="inline-block w-6 h-[3px] rounded-full" style={{ background: "linear-gradient(90deg, #E11D48 0%, #F43F5E 50%, #FB7185 100%)" }} />
            <span>Projected</span>
          </div>
          {hasActual && (
            <div className="flex items-center gap-2">
              <span className="inline-block w-6 h-[2px]" style={{ background: actualStroke }} />
              <span>Actual</span>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full border-2" style={{ borderColor: "#F43F5E", background: isDark ? "#0B0F1A" : "#FFFFFF" }} />
            <span>Milestone</span>
          </div>
        </div>
      </div>

      <div style={{ width: "100%", height: 280 }}>
        <ResponsiveContainer width="99%" height={280}>
          <ComposedChart data={data} margin={{ top: 4, right: 10, left: -8, bottom: 0 }}>
            <defs>
              <linearGradient id="proj-line" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#E11D48" />
                <stop offset="50%" stopColor="#F43F5E" />
                <stop offset="100%" stopColor="#FB7185" />
              </linearGradient>
              <linearGradient id="proj-area" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F43F5E" stopOpacity={0.24} />
                <stop offset="100%" stopColor="#F43F5E" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke={gridStroke} strokeDasharray="0" vertical={false} />
            <XAxis
              dataKey="label"
              stroke={axisText}
              fontSize={11}
              tickLine={false}
              axisLine={{ stroke: axisLine }}
              interval="preserveStartEnd"
              minTickGap={32}
            />
            <YAxis
              stroke={axisText}
              fontSize={11}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => {
                if (v >= 100) return `₹${(v / 100).toFixed(1)}Cr`;
                if (v >= 1) return `₹${v.toFixed(0)}L`;
                return `₹${Math.round(v * 100000).toLocaleString("en-IN")}`;
              }}
              width={64}
            />
            <Tooltip content={<CustomTooltip theme={theme} />} cursor={{ stroke: axisLine }} />
            <Area
              type="monotone"
              dataKey="projected"
              stroke="none"
              fill="url(#proj-area)"
              isAnimationActive
            />
            <Line
              type="monotone"
              dataKey="projected"
              stroke="url(#proj-line)"
              strokeWidth={2.5}
              dot={MilestoneDot({ isDark })}
              activeDot={{ r: 5, fill: "#F43F5E", stroke: isDark ? "#0B0F1A" : "#fff", strokeWidth: 2 }}
              isAnimationActive
            />
            {hasActual && (
              <Line
                type="monotone"
                dataKey="actual"
                stroke={actualStroke}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
